// Sistema de autenticação
export interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'agencia' | 'promotor';
  avatarUrl: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  user?: AuthUser;
  message?: string;
  redirectTo?: string;
}

// Credenciais válidas
const validCredentials = [
  {
    email: "adm@dooevents",
    password: "123456",
    user: {
      id: "admin-1",
      name: "Administrador",
      email: "adm@dooevents",
      role: "admin" as const,
      avatarUrl: "https://ui-avatars.com/api/?name=Admin&background=FF6B35&color=fff&size=128"
    },
    redirectTo: "/eventos"
  },
  {
    email: "agencia@dooevents", 
    password: "123456",
    user: {
      id: "agencia-1",
      name: "Agência",
      email: "agencia@dooevents",
      role: "agencia" as const,
      avatarUrl: "https://ui-avatars.com/api/?name=Agencia&background=4369F5&color=fff&size=128"
    },
    redirectTo: "/agencia"
  },
  {
    email: "promotor@dooevents",
    password: "123456", 
    user: {
      id: "promotor-1",
      name: "Promotor",
      email: "promotor@dooevents",
      role: "promotor" as const,
      avatarUrl: "https://ui-avatars.com/api/?name=Promotor&background=10B981&color=fff&size=128"
    },
    redirectTo: "/promotor"
  }
];

// Chaves para localStorage
const AUTH_TOKEN_KEY = 'dooevents_token';
const AUTH_USER_KEY = 'dooevents_user';

export const authService = {
  // Fazer login
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    // Simular delay de rede
    await new Promise(resolve => setTimeout(resolve, 800));
    
    const validCredential = validCredentials.find(
      cred => cred.email === credentials.email && cred.password === credentials.password
    );
    
    if (validCredential) {
      // Gerar token fake
      const token = `dooevents-token-${validCredential.user.role}-${Date.now()}`;
      
      // Salvar no localStorage
      localStorage.setItem(AUTH_TOKEN_KEY, token);
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(validCredential.user));
      
      return {
        success: true,
        user: validCredential.user,
        message: 'Login realizado com sucesso!',
        redirectTo: validCredential.redirectTo
      };
    } else {
      return {
        success: false,
        message: 'Email ou senha incorretos.'
      };
    }
  },

  // Fazer logout
  logout(): void {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_USER_KEY);
  },

  // Verificar se está logado
  isAuthenticated(): boolean {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    const user = localStorage.getItem(AUTH_USER_KEY);
    return !!(token && user);
  },

  // Obter usuário atual
  getCurrentUser(): AuthUser | null {
    const userStr = localStorage.getItem(AUTH_USER_KEY);
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch {
        return null;
      }
    }
    return null;
  },

  // Obter token atual
  getToken(): string | null {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  },

  // Verificar se usuário tem acesso a uma rota específica
  canAccessRoute(route: string): boolean {
    const user = this.getCurrentUser();
    if (!user) return false;

    // Administrador tem acesso a tudo
    if (user.role === 'admin') return true;

    // Agência tem acesso às rotas de agência e também às rotas administrativas de eventos
    if (user.role === 'agencia') {
      return route.startsWith('/agencia') || route.startsWith('/eventos-admin') || route === '/perfil';
    }

    // Promotor tem acesso às rotas de promotor e mecânicas
    if (user.role === 'promotor') {
      return route.startsWith('/promotor') || route.startsWith('/eventos-admin/mecanicas');
    }

    return false;
  }
};

// Importar tokenManager para usar o mesmo sistema de token que os eventos
import { tokenManager } from '@/lib/token-manager';

// Função para fazer requisições autenticadas
export async function authFetch(url: string, options: RequestInit = {}): Promise<Response> {
  // Usar tokenManager como os eventos fazem
  const token = tokenManager.getAccessToken() || authService.getToken();
  
  // Se não houver token, retornar dados vazios
  if (!token && import.meta.env.DEV) {
    console.warn('No auth token, returning empty data for:', url);
    
    // Retornar array vazio para todas as requisições sem autenticação
    const emptyData = { data: [], meta: { total: 0, page: 1, limit: 10 } };
    
    return new Response(JSON.stringify(emptyData), {
      status: 200,
      headers: { 'Content-Type': 'application/json' }
    });
  }
  
  const headers = {
    ...options.headers,
    'Authorization': token ? `Bearer ${token}` : '',
  };
  
  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });
    
    // Se receber 401, fazer logout automático
    if (response.status === 401) {
      authService.logout();
      window.location.href = '/login';
      throw new Error('Sessão expirada');
    }
    
    return response;
  } catch (error) {
    // Em caso de erro de rede em desenvolvimento, retornar dados vazios
    if (import.meta.env.DEV) {
      console.warn('Network error, returning empty data for:', url);
      return new Response(JSON.stringify({ data: [], meta: { total: 0, page: 1, limit: 10 } }), {
        status: 200,
        headers: { 'Content-Type': 'application/json' }
      });
    }
    throw error;
  }
}